/**
 * The homeowner's view of an installation (the page behind the board's QR code): for each
 * board, each RCD and the circuits under it — letter, what it feeds in plain words, the rooms
 * its points are in, its rating — so "which breaker for the kitchen?" and "what is under this
 * RCD?" have an answer long after the inspection. Rooms come from the position plans (the room
 * each point is drawn in); without a plan, the circuit's description stands alone.
 */
import type { Element, Id, Project } from '@elec/domain';
import { analyse, boardRootSheet, type Analysis, type CircuitInfo, type PointKind } from '@elec/analysis';
import { pointRooms, sensitivityMa } from './core.ts';

export interface HomeCircuit {
  code: string;
  label: string;
  /** "C16 A", "B20 A". */
  protection: string;
  /** Room names (or room types when unnamed), in plan order, each once. */
  rooms: string[];
  /** How many sockets, lights, appliances… it feeds. */
  points: Partial<Record<PointKind, number>>;
}

export interface HomeRcd {
  tag: string;
  sensitivityMa: number | undefined;
  circuits: HomeCircuit[];
}

export interface HomeBoard {
  name: string;
  /** Circuits under each RCD, nearest RCD first; circuits with none under `rcd: null`. */
  groups: Array<{ rcd: HomeRcd | null; circuits: HomeCircuit[] }>;
}

export function homeGuide(project: Project, a: Analysis = analyse(project)): HomeBoard[] {
  const rooms = pointRooms(project);
  const boards = new Map<
    Id,
    HomeBoard & { byRcd: Map<string, { rcd: HomeRcd | null; circuits: HomeCircuit[] }> }
  >();
  for (const c of a.circuits) {
    if (c.circuit?.spare) continue;
    const root = boardRootSheet(project, a, c.sheet.id);
    const sheet = project.sheets[root];
    let b = boards.get(root);
    if (!b) {
      b = { name: sheet?.name ?? '', groups: [], byRcd: new Map() };
      boards.set(root, b);
    }
    const r = c.rcds[0];
    const key = r?.id ?? '';
    let g = b.byRcd.get(key);
    if (!g) {
      g = { rcd: r ? rcdOf(r) : null, circuits: [] };
      b.byRcd.set(key, g);
      b.groups.push(g);
    }
    const hc = circuitOf(c, rooms);
    g.circuits.push(hc);
    g.rcd?.circuits.push(hc);
  }
  return [...boards.entries()]
    .sort((x, y) => (project.sheets[x[0]]?.order ?? 0) - (project.sheets[y[0]]?.order ?? 0))
    .map(([, b]) => ({
      name: b.name,
      groups: b.groups
        .map((g) => ({
          ...g,
          circuits: g.circuits.sort((x, y) => x.code.localeCompare(y.code, undefined, { numeric: true })),
        }))
        .sort((x, y) =>
          (x.circuits[0]?.code ?? '').localeCompare(y.circuits[0]?.code ?? '', undefined, { numeric: true }),
        ),
    }));
}

const rcdOf = (e: Element): HomeRcd => {
  const s = sensitivityMa(e);
  return { tag: e.tag, sensitivityMa: Number.isFinite(s) ? s : undefined, circuits: [] };
};

function circuitOf(c: CircuitInfo, rooms: Map<string, { type: string; name: string }>): HomeCircuit {
  const names: string[] = [];
  const points: HomeCircuit['points'] = {};
  for (const p of c.points) {
    const n = typeof p.element.props.count === 'number' ? p.element.props.count : 1;
    points[p.kind] = (points[p.kind] ?? 0) + n;
    const r = rooms.get(p.element.tag);
    const name = r ? r.name || r.type : '';
    if (name && !names.includes(name)) names.push(name);
  }
  const curve = String(c.device.props.curve ?? (c.device.symbolId.includes('fuse') ? '' : 'C'));
  return {
    code: c.code || c.device.tag,
    label: c.circuit?.label ?? '',
    protection: c.ratingA ? `${curve}${c.ratingA} A` : '',
    rooms: names,
    points,
  };
}
